import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faTimes, faEye, faSave } from '@fortawesome/free-solid-svg-icons';
import { 
  Card, 
  Form, 
  Button, 
  Table, 
  Badge, 
  Pagination,
  Row,
  Col,
  Modal,
  FormSelect,
  Spinner
} from '@themesberg/react-bootstrap';
import api from '../../utils/api';

const DEFAULT_STORE_URL = 'https://play.google.com/store/apps/details?id=com.evearkadaslik.sohbet';

const REJECTION_REASONS = [
  { value: '1', label: 'Kötü Niyetli Yorum' },
  { value: '2', label: 'Gönderilen Fotoğraf Yayınlanan Yorumu Net Olarak İçermiyor' },
  { value: '3', label: 'Uygunsuz Görsel' },
  { value: '4', label: 'Spam veya Aldatmaya Yönelik Hareket' },
  { value: '5', label: 'Hesabınız Daha Önce Bu Kampanyadan Faydalandı' }
];

export default () => {
  // Değerlendirme İstekleri State
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [rejectingRequestId, setRejectingRequestId] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');

  // Değerlendirme Ayarları State
  const [settings, setSettings] = useState(() => ({
    enabled: false,
    reward: 20,
    storeUrl: DEFAULT_STORE_URL
  }));
  const [saving, setSaving] = useState(false);

  // Değerlendirme isteklerini yükle
  useEffect(() => {
    const fetchRequests = async () => {
      try {
        const res = await api.get('/reviews', { params: { status: 'pending' } });
        setRequests(res.data?.reviews || []);
      } catch (error) {
        console.error("Error loading review requests:", error);
        alert("Değerlendirme istekleri yüklenemedi: " + (error.response?.data?.error || error.message));
      } finally {
        setLoading(false);
      }
    };
    fetchRequests();
  }, []);

  // Değerlendirme ayarlarını yükle
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await api.get('/settings/reviewCampaign');
        const data = res.data?.value ?? res.data;
        if (data && typeof data === 'object') {
          setSettings(prev => ({
            ...prev,
            enabled: data.enabled || false,
            reward: data.reward ?? 20,
            storeUrl: data.storeUrl || DEFAULT_STORE_URL
          }));
        }
      } catch (error) {
        console.error("Error loading review settings:", error);
      }
    };
    fetchSettings();
  }, []);

  // Onayla
  const handleApprove = async (requestId, userId, reward) => {
    try {
      await api.post('/reviews/approve', { requestId, userId, reward });
      setRequests(prev => prev.filter(r => r.id !== requestId));
    } catch (error) {
      console.error("Error approving review request:", error);
      alert("Onaylama işlemi başarısız: " + (error.response?.data?.error || error.message));
    }
  };

  // Reddet
  const handleReject = async () => {
    if (!rejectionReason) {
      alert("Lütfen ret sebebi seçiniz.");
      return;
    }

    const requestId = rejectingRequestId;
    try {
      await api.post('/reviews/reject', { requestId, reason: rejectionReason });
      setRequests(prev => prev.filter(r => r.id !== requestId));

      setShowRejectModal(false);
      setRejectingRequestId(null);
      setRejectionReason('');
    } catch (error) {
      console.error("Error rejecting review request:", error);
      alert("Reddetme işlemi başarısız: " + (error.response?.data?.error || error.message));
    }
  };

  // Ayarları kaydet
  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await api.post('/settings/update', {
        reviewCampaign: {
          enabled: settings.enabled,
          reward: Number(settings.reward) || 0,
          storeUrl: settings.storeUrl
        }
      });
      alert("Ayarlar başarıyla kaydedildi.");
    } catch (error) {
      console.error("Error saving settings:", error);
      alert("Ayarlar kaydedilirken hata oluştu: " + (error.response?.data?.error || error.message));
    } finally {
      setSaving(false);
    }
  };

  // Sayfalama
  const totalPages = Math.ceil(requests.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentRequests = requests.slice(startIndex, endIndex);

  return (
    <>
      <div className="py-4">
        <h4>Değerlendirmeler</h4>
        <p className="text-muted mb-0">Değerlendirme istekleri ve ayarları</p>
      </div>

      {/* Değerlendirme İstekleri */}
      <Card className="mb-4">
        <Card.Header>
          <h5 className="mb-0">Değerlendirme İstekleri</h5>
          <p className="text-muted mb-0">Toplam {requests.length} istek</p>
        </Card.Header>
        <Card.Body>
          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
            </div>
          ) : requests.length === 0 ? (
            <p className="text-muted text-center py-4">Henüz değerlendirme isteği bulunmuyor.</p>
          ) : (
            <>
              <Table responsive className="table-hover">
                <thead>
                  <tr>
                    <th>Kullanıcı</th>
                    <th>Kanıt Resmi</th>
                    <th>Durum</th>
                    <th>Tarih</th>
                    <th>İşlemler</th>
                  </tr>
                </thead>
                <tbody>
                  {currentRequests.map((request) => (
                    <tr key={request.id}>
                      <td>
                        <div>
                          <strong>{request.userName || 'Bilinmeyen'}</strong>
                          <br />
                          <small className="text-muted">{request.userId}</small>
                        </div>
                      </td>
                      <td>
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => {
                            setSelectedImage(request.screenshotUrl);
                            setShowImageModal(true);
                          }}
                        >
                          <FontAwesomeIcon icon={faEye} className="me-2" />
                          Resmi Görüntüle
                        </Button>
                      </td>
                      <td>
                        <Badge bg="warning">Beklemede</Badge>
                      </td>
                      <td>
                        {request.createdAt
                          ? new Date(request.createdAt).toLocaleString('tr-TR')
                          : 'Tarih yok'}
                      </td>
                      <td>
                        <Button
                          variant="success"
                          size="sm"
                          className="me-2"
                          onClick={() => handleApprove(request.id, request.userId, request.reward || settings.reward)}
                        >
                          <FontAwesomeIcon icon={faCheck} className="me-1" />
                          Onayla
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => {
                            setRejectingRequestId(request.id);
                            setShowRejectModal(true);
                          }}
                        >
                          <FontAwesomeIcon icon={faTimes} className="me-1" />
                          Reddet
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              {/* Sayfalama */}
              <Row className="mt-3">
                <Col md={6}>
                  <FormSelect
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    style={{ width: 'auto', display: 'inline-block' }}
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                  </FormSelect>
                  <span className="ms-2 text-muted">göster</span>
                </Col>
                <Col md={6} className="text-end">
                  <Pagination className="mb-0">
                    <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
                    <Pagination.Prev onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} />
                    <Pagination.Item active>{currentPage}</Pagination.Item>
                    <Pagination.Next onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} />
                    <Pagination.Last onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} />
                  </Pagination>
                </Col>
              </Row>
            </>
          )}
        </Card.Body>
      </Card>

      {/* Değerlendirme Ayarları */}
      <Card>
        <Card.Header>
          <h5 className="mb-0">Değerlendirme Ayarları</h5>
        </Card.Header>
        <Card.Body>
          <Row className="mb-3">
            <Col md={12}>
              <Form.Group>
                <Form.Label>Aç/Kapat</Form.Label>
                <div>
                <Form.Check
                  type="switch"
                  id="enabled-switch"
                  label={settings?.enabled ? "Açık" : "Kapalı"}
                  checked={settings?.enabled || false}
                  onChange={(e) => {
                    const checked = e?.target?.checked || false;
                    setSettings(prev => ({
                      ...(prev || {}),
                      enabled: checked
                    }));
                  }}
                />
                </div>
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={12}>
              <Form.Group>
                <Form.Label>Değerlendirme URL</Form.Label>
                <Form.Control
                  type="text"
                  value={settings?.storeUrl || ""}
                  onChange={(e) => {
                    const value = e?.target?.value || "";
                    setSettings(prev => ({
                      ...(prev || {}),
                      storeUrl: value
                    }));
                  }}
                  placeholder="https://play.google.com/store/apps/details?id=..."
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={12}>
              <Form.Group>
                <Form.Label>Ödül (Jeton)</Form.Label>
                <Form.Control
                  type="number"
                  value={settings?.reward || 20}
                  onChange={(e) => {
                    const value = e?.target?.value || "0";
                    const numValue = Number(value) || 0;
                    setSettings(prev => ({
                      ...(prev || {}),
                      reward: numValue
                    }));
                  }}
                  min="0"
                />
                <Form.Text className="text-muted">
                  Uygulamada gösterilecek: "Uygulamayı Değerlendir {settings.reward} Jeton Kazan"
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={12} className="text-end">
              <Button
                variant="primary"
                onClick={handleSaveSettings}
                disabled={saving}
              >
                {saving ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Kaydediliyor...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faSave} className="me-2" />
                    Kaydet
                  </>
                )}
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Resim Görüntüleme Modal */}
      <Modal show={showImageModal} onHide={() => setShowImageModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Kanıt Resmi</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedImage && (
            <img src={selectedImage} alt="Kanıt Resmi" style={{ width: '100%', height: 'auto' }} />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowImageModal(false)}>
            Kapat
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Reddetme Modal */}
      <Modal show={showRejectModal} onHide={() => {
        setShowRejectModal(false);
        setRejectionReason('');
      }}>
        <Modal.Header closeButton>
          <Modal.Title>Ret Sebebi Seçiniz</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Ret Sebebi:</Form.Label>
            <FormSelect
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
            >
              <option value="">Seçiniz...</option>
              {REJECTION_REASONS.map(reason => (
                <option key={reason.value} value={reason.value}>
                  {reason.value}- {reason.label}
                </option>
              ))}
            </FormSelect>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowRejectModal(false);
              setRejectionReason('');
            }}
          >
            İptal
          </Button>
          <Button variant="danger" onClick={handleReject}>
            Reddet
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

